import { Dialog, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";

import { useHeaderCollapse } from "@/components/layout/private/main/components/Header/HeaderCollapseContext";
import {
  getMobileContentTopInset,
  getMobileToolbarHeight,
} from "@/components/layout/private/main/components/Header/headerLayoutUtils";
import { isReactNativeWebView } from "@/utils/useWebViewBridge";

interface MobileProps {
  title: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Segunda etapa de fluxo: seta à esquerda do título (ex.: voltar aos dados fitness). */
  onBack?: () => void;
  subtitle?: string;
  /** Soma ao z-index do modal — necessário acima de `MobileBottomSheet` (ex.: questionários). */
  zIndexOffset?: number;
}

/**
 * Tela mobile exclusiva em fullscreen — documentos, agenda, família, etc.
 * Preenche a área abaixo do header do app (sem card/balão flutuante).
 * Formulários curtos usam `MobileBottomSheet` via `CommonDialog`.
 */
export function ScreenMobile({
  title,
  open,
  onClose,
  children,
  onBack,
  subtitle,
  zIndexOffset = 0,
}: MobileProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const { mobileHeaderSpacerHeight, isHeaderCollapsed } = useHeaderCollapse();
  const isRnWebView = isReactNativeWebView();

  /**
   * Espaço sob o AppBar.
   * No fluxmedportable o safe-area já vem do nativo (`paddingTop: insets.top`).
   * Se ainda somarmos `env(safe-area-inset-top)` aqui, sobra uma brecha no emulador
   * (no Chrome responsivo esse env costuma ser 0 — por isso “lá funciona”).
   */
  const headerTopInset = !isMobileLayout
    ? "0px"
    : isRnWebView
      ? `${getMobileToolbarHeight(isHeaderCollapsed, theme)}px`
      : mobileHeaderSpacerHeight ?? getMobileContentTopInset(theme);

  // Acima do Dialog fullscreen do PrivateMainLayout (modal), para a tela exclusiva
  // ficar visível; o topo transparente (pt) deixa o AppBar aparecer.
  const dialogZIndex =
    zIndexOffset > 0
      ? theme.zIndex.modal + zIndexOffset
      : theme.zIndex.modal + 1;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      disableScrollLock
      BackdropProps={{
        sx: {
          top: headerTopInset,
        },
      }}
      sx={{
        zIndex: dialogZIndex,
        "& .MuiDialog-container": {
          alignItems: "stretch",
          pt: headerTopInset,
        },
        "& .MuiDialog-paper": {
          m: 0,
          borderRadius: 0,
          width: "100%",
          maxWidth: "100%",
          height: "100%",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "background.default",
        },
      }}
    >
      <Stack
        direction="column"
        width="100%"
        height="100%"
        sx={{ minHeight: 0 }}
      >
        {/* Barra no topo: X para fechar; seta só quando a tela informa onBack. */}
        <Stack
          width="100%"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexShrink: 0,
            minHeight: 56,
            px: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {onBack ? (
              <IconButton
                size="small"
                aria-label="Voltar"
                onClick={onBack}
                sx={{ color: "neutral1000.main", flexShrink: 0 }}
              >
                <ArrowBack />
              </IconButton>
            ) : null}
            <Stack sx={{ flex: 1, minWidth: 0, px: 1 }}>
              <Typography
                variant="subtitle2"
                color="neutral700.main"
                fontWeight={600}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {subtitle}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          <IconButton
            sx={{ color: "neutral1000.main", flexShrink: 0 }}
            onClick={onClose}
            aria-label="Fechar"
          >
            <Close />
          </IconButton>
        </Stack>

        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {children}
        </Stack>
      </Stack>
    </Dialog>
  );
}
